import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService, TreeNode, MenuItem } from 'primeng/api';
import { EMPTY, Observable, Observer, Subject } from 'rxjs';
import { catchError, map, take, finalize, filter, switchMap } from 'rxjs/operators';
import { Status } from './models/status';

import { MainState } from './main.state';
import { DisplayType, FileProject, FileSearchRequest, IFile, IFileInfo, ImageInfo, RawFileInfo } from './models/file-info';
import { FileInfoFactory } from './models/file-info/file-info.factory';
import { FilesService } from '../services/files.service';
import { ProcessService } from '../services/process.service';
import {
  CropRequest,
  Instruction,
  ProcessType,
  QuantRegion,
  QuantRequest,
  Request,
  ValidationType,
  ZoomRequest
} from './models/request';
import { Polygon } from '@jax-data-science/sci-image-visualizer';
import { TOOL_BY_PROCESS_TYPE } from './models/jit-tool-registry.helpers';
import { StringResponse } from './models/status';
import { TableData } from './models/table-data';
import { ProcessStatusFacade } from './components/process/process-status/process-status.facade';
import { DownloadService } from '../download/download.service';
import { PAGE_SIZE } from './main.utilities';
import { SpatialOmicsService } from '../services/spatial-omics.service';
import { AnnotationsService } from '../services/annotations.service';

@Injectable({
  providedIn: 'root',
})
export class MainFacade {

  private diagramComponent: any = null;

  // One long-lived pipeline for every result-URI resolve (Results tab row
  // clicks, Queue tab "View Results", deep links). Before this, each call to
  // showResultInViewer() started its own independent subscribe() with
  // nothing to cancel the last one - clicking through several results in a
  // row left that many getFileInfoFromUri() calls in flight, each racing to
  // call setSelectedNode() (and, transitively, restart image loading/cache
  // polling for whichever one it was) whenever it happened to land, visibly
  // freezing the Results tab under a few quick clicks. switchMap
  // unsubscribes the previous call (HttpClient turns that into an XHR
  // abort) the moment a newer one arrives - the same fix already applied to
  // FilesService.imageOpenRequest$ for the metadata fetch one stage further
  // downstream (jit-ui#119); this closes the resolve-stage gap in front of
  // it. Only the UI-side job is cancelled - whatever the server does for an
  // abandoned request (e.g. cache-filling) is unaffected, as intended.
  private readonly openResultRequest$ = new Subject<string>();

  /** Fires once per openResultPath()/showResultInViewer() attempt that
   *  SETTLES - success or error - regardless of whether that outcome ever
   *  flips isTreeLoading$/isFileLoading$/isImageLoading$. A caller (e.g.
   *  ResultSearchComponent's busy cursor) that's waiting for one of those
   *  three flags to show something is happening has no way to know the
   *  resolve attempt already finished when it errors, or when it succeeds
   *  but the resolved file starts none of them - before this, such a caller
   *  could only fall back to a hardcoded timeout (jit-ui#136 review). Safe
   *  to race alongside the loading flags: they flip synchronously inside
   *  setSelectedNode() below, so a `take(1)` racing both already resolves
   *  via a real loading flag first whenever one actually starts - this only
   *  matters when none of them do. */
  private readonly resultResolutionSettled$ = new Subject<true>();

  constructor(private mainState: MainState,
              private filesService: FilesService,
              private processService: ProcessService,
              private downloadService: DownloadService,
              private messageService: MessageService,
              private processStatusFacade: ProcessStatusFacade,
              private spatialOmics: SpatialOmicsService,
              private annotations: AnnotationsService) {

    this.mainState.getSelectedProject$().subscribe(
      (project) => {
        this.mainState.setSelectedNode(null);
        this.mainState.setRootNodes([]);
        if (project) {
          this.loadChildren(project.rawData);
        }
      }
    );
    mainState.getDiagramData$().subscribe(
      (data) => {
        this.diagramComponent = data;
      }
    );

    // catchError sits INSIDE switchMap (returning EMPTY, not rethrowing) so
    // one request's failure can't terminate this subscription for the
    // lifetime of the service, which would silently kill every later click.
    this.openResultRequest$.pipe(
      switchMap(gcsPath =>
        this.filesService.getFileInfoFromUri(gcsPath).pipe(
          catchError(err => {
            const msg = err?.error?.message ?? err?.message ?? 'Could not resolve the result file.';
            this.errorMessage('Could Not Open Result', msg);
            this.resultResolutionSettled$.next(true);
            return EMPTY;
          }),
        )
      ),
    ).subscribe(raw => {
      const info = FileInfoFactory.getFileInfo(raw);

      // Shaped like a real file-tree leaf node (see
      // MainUtilities.createDefaultTreeNode: leaf true + children [] for a
      // File, .parent set by whoever loaded that folder), not just `{ data:
      // info }` - every process tool component (Cellpose, Segmentation,
      // YoloSegdetect, StainAdapter, RetinalLayer, Deconvolution, ...)
      // subscribes to getSelectedNode$() and reads node.leaf/node.children/
      // node.parent?.data?.relPath to build what it actually submits when
      // run. Without this, the node was "selected" (shown in the viewer)
      // but invisible to those tools - Cellpose's inputImages stayed empty,
      // others read a blank or wrong path. No extra network round trip: the
      // parent's relPath is just the file's own already-resolved relPath
      // with its last /segment trimmed off.
      const relPath = info.relPath ?? '';
      const slash = relPath.lastIndexOf('/');
      const parentPath = slash >= 0 ? relPath.substring(0, slash) : '';
      const parentNode: TreeNode<IFileInfo> = {
        data: { relPath: parentPath, project: info.project } as IFileInfo,
      };
      this.mainState.setSelectedNode({
        data: info,
        leaf: true,
        children: [],
        parent: parentNode,
      } as TreeNode<IFileInfo>);
      this.resultResolutionSettled$.next(true);
    });
  }

  /**
   * calls the service to fetch all active projects, transforms the
   * received data into entities and saves the projects in observable
   * note: executes post authentication
   */
  loadProjects() {
    this.filesService.getProjects().subscribe((rawFile) => {
      const projects: FileProject[] = [];
      rawFile.forEach(rawFile => {
        projects.push(FileInfoFactory.getFileInfo(rawFile));
      });
      this.mainState.setProjects$(projects);
    });
  }

  createMainTabMenu(items: MenuItem[]) {
    this.mainState.setTabMenuItems$(items);
  }

  getMainTabMenu$(): Observable<MenuItem[]> {
    return this.mainState.getMainTabMenu$();
  }

  selectTabMenuItemL$(item: MenuItem) {
    this.mainState.setSelectedTabItemL$(item);
  }

  selectTabMenuItemR$(item: MenuItem) {
    this.mainState.setSelectedTabItemR$(item);
  }

  /**
   * Activate a right-panel tab by id (e.g. 'regions'). Resolved against the
   * current itemsTabMenu held in MainState. No-op if the id isn't found.
   */
  selectTabMenuItemRById(id: string) {
    this.mainState.getMainTabMenu$().pipe(take(1)).subscribe(items => {
      const item = (items ?? []).find(i => i.id === id);
      if (item) this.mainState.setSelectedTabItemR$(item);
    });
  }

  getSelectedTabMenuItemL$(): Observable<MenuItem | null> {
    return this.mainState.getSelectedTabMenuItemL$();
  }

  getSelectedTabMenuItemR$(): Observable<MenuItem | null> {
    return this.mainState.getSelectedTabMenuItemR$();
  }

  getProjects$(): Observable<FileProject[]> {
    return this.mainState.getProjects$();
  }

  getSelectedProject$(): Observable<FileProject | null> {
    return this.mainState.getSelectedProject$();
  }

  getFileInfo$(): Observable<IFileInfo[]> {
    return this.mainState.getFileInfo$();
  }

  getSelectedNode$(): Observable<TreeNode<IFileInfo> | null> {
    return this.mainState.getSelectedNode$();
  }

  getFileContent$(): Observable<string | null> {
    return this.mainState.getFileContent$();
  }

  getFileLoading$(): Observable<boolean> {
    return this.mainState.isFileLoading$();
  }

  getTableData$(): Observable<TableData> {
    return this.mainState.getTableData$();
  }

  isTreeLoading$(): Observable<boolean> {
    return this.mainState.isFileTreeLoading$();
  }

  isFileLoading$(): Observable<boolean> {
    return this.mainState.isFileLoading$();
  }

  isImageLoading$() {
    return this.mainState.isImageLoading$();
  }

  /** See resultResolutionSettled$'s doc above - fires once per
   *  openResultPath()/showResultInViewer() attempt that settles, whether or
   *  not that outcome starts any of the three loading flags above. */
  getResultResolutionSettled$(): Observable<true> {
    return this.resultResolutionSettled$.asObservable();
  }

  setImageLoading$(isImageLoading: boolean) {
    this.mainState.setImageLoading(isImageLoading);
  }

  getImageLoadingMessage() {
    return this.mainState.getImageLoadingMessage$();
  }
  setImageLoadingMessage(imageLoadingMessage: string) {
    this.mainState.setImageLoadingMessage(imageLoadingMessage);
  }

  getCacheProgress$() {
    return this.mainState.getCacheProgress$();
  }

  isImageCached() {
    return this.mainState.isImageCached$();
  }

  isZoom() {
    return this.mainState.isZoom$();
  }
  setZoom(zoom: boolean) {
    this.mainState.setZoom(zoom);
  }

  getFilename() {
    return this.mainState.getFilename();
  }
  setFilename(filename: string) {
    this.mainState.setFilename(filename);
  }

  isLoadingError$(): Observable<boolean> {
    return this.mainState.isLoadingError$();
  }

  getProcessing$(): Observable<boolean> {
    return this.mainState.isProcessing$();
  }

  getProcessingStatus$() {
    return this.mainState.getProcessingStatus$();
  }

  getPreviewUrl$(): Observable<string | null> {
    return this.mainState.getPreviewUrl$();
  }

  getPreviewSlicesUrls$(): Observable<string[] | undefined> {
    return this.mainState.getPreviewSlicesUrls$();
  }

  getImageSize$(): Observable<any> {
    return this.mainState.getImageSize$();
  }

  setImageInfo(imageInfo: ImageInfo) {
    this.mainState.setImageInfo$(imageInfo);
  }
  getImageInfo$(): Observable<ImageInfo | null> {
    return this.mainState.getImageInfo$();
  }

  selectProject(project: FileProject) {
    this.mainState.setSelectedProject$(project);
  }

  setDiagram(diagram: any) {
    this.mainState.setDiagramData(diagram);
  }

  getDiagram(): any {
    return this.diagramComponent;
  }

  getSelectedFileInfo(): IFileInfo | undefined {
    return this.filesService.getSelectedFileInfo();
  }

  isFileSelected(): Observable<boolean> {
    return this.filesService.isFileSelected$();
  }

  setPanelWidth(value: number) {
    this.mainState.setPanelWidth(value);
  }
  getPanelWidth(): Observable<number> {
    return this.mainState.getPanelWidth();
  }

  isSelectedFileFolder$(): Observable<boolean | null> {
    return this.mainState.getSelectedFile$().pipe(
      map((info) => {
        return info == null ? null : info?.type == 'Folder';
      })
    );
  }

  /**
   * Whether there's something processible selected right now: a supported
   * image file, any folder (a Rule watches a folder for future files, so it
   * shouldn't require one to already contain images - see JIT-Rules.md), or
   * any other file. Tools now take files that aren't images in the browser's
   * sense: a `.zip` or `.zarr.zip`, a `.nii`/`.nii.gz` or `.nrrd` volume, an
   * `.h5ad` (Convert prepares spatial omics from them), so a file node is
   * enough. Independent of which tools are actually installed/active on the
   * server - see ProcessComponent.getAvailableGroups()/filterByActiveTools for
   * that, unaffected by this check.
   */
  canProcessSelection$(): Observable<boolean> {
    return this.mainState.getSelectedFile$().pipe(
      map((info) => {
        if (info == null) {
          return false;
        }
        if (info instanceof IFile) {
          return true;
        }
        return info.type === 'Folder' || info.type === 'File';
      })
    );
  }

  getRootNodes$(): Observable<TreeNode<IFileInfo>[]> {
    return this.mainState.getRootNodes$();
  }

  /** See MainState.getHighlightNode$() - FileTreeComponent subscribes to
   *  highlight a node without re-driving FileViewerComponent. */
  getHighlightNode$(): Observable<TreeNode<IFileInfo>> {
    return this.mainState.getHighlightNode$();
  }

  getValidationMessage$(): Observable<string | null> {
    return this.mainState.getValidationMessage$();
  }

  openZoomSession(file: IFileInfo): Observable<any> {
    return this.filesService.openZoomSession(file);
  }

  getZoomRegion(zoomRequest: ZoomRequest): Observable<any> {
    return this.filesService.zoomOnRegion(zoomRequest);
  }

  selectDisplayType(displayType: DisplayType | null) {
    this.filesService.selectDisplayType(displayType);
  }

  /** Explicit "Load as Stack" action from the file-tree context menu — load
   *  the numbered image series `node`'s image belongs to as one stack. */
  loadAsStack(node: TreeNode<IFileInfo>): void {
    this.filesService.loadAsStack(node);
  }

  updateNode(node: any): void {
    this.mainState.setSelectedNode(node);
    if (node == null || node.data == null) {
      return;
    }
    // Clear children and reload from page 0 (disk may have changed).
    node.children.length = 0;
    this.loadChildren(node.data.rawData, node, 0);
  }

  loadMoreChildren(folderNode: TreeNode, nextPage: number): void {
    if (!folderNode || !folderNode.data) return;
    // Convert the "loadmore" sentinel to a "loadingmore" spinner sentinel while the request is in flight.
    if (folderNode.children && folderNode.children.length > 0) {
      const last = folderNode.children[folderNode.children.length - 1];
      if (last.data?.isLoadMore) {
        last.type = 'loadingmore';
      }
    }
    this.loadChildren(folderNode.data.rawData, folderNode, nextPage);
  }

  loadChildren(parent: any, targetNode?: TreeNode, page: number = 0): void {
    this.mainState.setFileTreeLoading(true);
    this.filesService.getChildren(parent, page, PAGE_SIZE)
      .pipe(finalize(() => this.mainState.setFileTreeLoading(false)))
      .subscribe({
      next: files => {
        const fileArr: IFileInfo[] = files.map(f => FileInfoFactory.getFileInfo(f));
        if (!targetNode) {
          // Root project loading — use the existing fileInfo$ pipeline.
          this.mainState.setFileInfo$(fileArr);
        } else {
          // Folder child loading — mutate the node directly.
          // Remove the loadingmore spinner sentinel before appending new results.
          if (targetNode.children && targetNode.children.length > 0) {
            const last = targetNode.children[targetNode.children.length - 1];
            if (last.type === 'loadingmore') {
              targetNode.children.pop();
            }
          }
          const newNodes = this.filesService.createNodes(fileArr);
          newNodes.forEach(n => n.parent = targetNode);
          targetNode.children!.push(...newNodes);
          if (files.length === PAGE_SIZE) {
            const sentinel = this.filesService.createLoadMoreNode(parent, page + 1);
            sentinel.parent = targetNode;
            targetNode.children!.push(sentinel as TreeNode);
          }
        }
      },
      error: (err: HttpErrorResponse) => {
        // Clear the load-more spinner sentinel so the folder isn't stuck mid-page.
        if (targetNode?.children?.length) {
          const last = targetNode.children[targetNode.children.length - 1];
          if (last.type === 'loadingmore') {
            targetNode.children.pop();
          }
        }
        const msg = err?.error?.message ?? err?.message ?? 'Could not open folder';
        this.errorMessage('Could not open folder', msg);
        console.error('getChildren failed', err);
      }
    });
  }

  nodeSelect(node: TreeNode<IFileInfo>): void {
    this.mainState.setSelectedNode(node);
  }

  /** "Open as annotations" from the file-tree context menu: routed as the spatial open is. */
  openAsAnnotations(node: TreeNode<IFileInfo>): void {
    if (!node?.data) return;
    this.annotations.requestOpen(node.data);
    this.mainState.setSelectedNode(node);
  }

  /** "Open in Spatial Omics plot mode" from the file-tree context menu. Routes through
   *  node selection, as "Load as Stack" does, so the viewer mounts the visualizer. */
  openInSpatialOmics(node: TreeNode<IFileInfo>): void {
    if (!node?.data) return;
    this.spatialOmics.requestOpen(node.data);
    this.mainState.setSelectedNode(node);
  }

  /**
   * Gets the currently selected file information (or 'undefined')
   * and fetches that file's content from the source service.
   */
  fetchContent(): void {
    const selectedFile = this.filesService.getSelectedFileInfo();
    const file: IFileInfo | undefined = selectedFile;

    this.mainState.setFileLoading(true);

    if (file) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const extension = (file as any).fileExtension;
      const isJsonLike = extension === 'json' || extension === 'geojson';
      this.filesService.getContent(file.rawData).subscribe({
        next: (content: string) => {
          if (isJsonLike) {
            try {
              const object = JSON.parse(content);
              // prettify JSON
              content = JSON.stringify(object, null, 4);
            } catch (err) {
              console.error(err);
            }
          }
          this.mainState.setFileContent(content);
          this.mainState.setFileLoading(false);
        },
        error: (error) => {
          console.error(`error loading file content: ${error.message}`);
          this.mainState.setFileLoading(false);
        },
        complete: () => {
          this.mainState.setFileLoading(false);
        }
      });
    }
  }

  fetchTableData(): void {
    const file: IFileInfo | undefined = this.filesService.getSelectedFileInfo();

    this.mainState.setFileLoading(true);

    if (file) {
      this.filesService.getTableData(file.rawData).subscribe({
        next: (data) => {
          this.mainState.setTableData(data);
        },
        error: (error) => {
          console.error(`error loading table data: ${error.message}`);
        },
        complete: () => {
          this.mainState.setFileLoading(false);
        }
      })
    }
  }

  deleteSelected() {
    const node = this.filesService.getSelectedNode();
    if (node) {
      this.deleteNode(node);
    }
  }

  deleteNode(node: TreeNode<IFileInfo>) {
    const fileInfo = node.data;
    if (!fileInfo) return;
    this.filesService.deleteFile(fileInfo.rawData).subscribe({
      next: () => {
        this.messageService.add({ key: 'app-toast', severity: 'success', summary: 'Delete Success', detail: `"${fileInfo.name}" was successfully deleted.` });
      },
      error: () => {
        this.messageService.add({ key: 'app-toast', severity: 'warn', summary: 'Cannot Delete', detail: `"${fileInfo.name}" could not be deleted.` });
      },
      complete: () => {
        if (node.parent && node.parent.children) {
          const index = node.parent.children!.indexOf(node);
          if (index >= 0) {
            node.parent.children!.splice(index, 1);
          }
        } else {
          const rootNodes = this.filesService.getRootNodes();
          const index = rootNodes.indexOf(node);
          if (index >= 0) {
            rootNodes.splice(index, 1);
            this.filesService.setRootNodes(rootNodes);
          }
        }
      }
    });
  }

  interuptPreview(): void {
    console.log('interrupt preview');
  }

  processSelected(): void {
    console.log('process selected');
  }

  // TODO - can remove as everything is run and monitored in activity queue
  queueProcess(request: Request): void {
    this.runProcess(request);
  }

  /**
   * Run a process with a given Request.
   *
   * @param request
   * @param fileOverride  submit against this file/folder instead of the
   *                      currently selected one - used for the directory
   *                      wildcard a user picks in the folder-submit
   *                      confirmation dialog (jit-ui#131).
   */
  runProcess(request: Request, fileOverride?: RawFileInfo): Observable<any> {
    this.configRequest(request);
    const processType = this.hackRequest(request);
    const instruction = this.createInstruction(request, fileOverride);
    if (instruction) {
      return this.validateAndRun(processType, instruction);
    } else {
      // Return an observable that errors immediately if instruction is null
      return new Observable(observer => {
        observer.error('No instruction could be created');
      });
    }
  }

  /**
   * Run Cellpose on a whole-slide image and/or on drawn regions by first
   * cropping, then segmenting — submitted as a single COMPOUND workflow.
   *
   * Why this exists: the Cellpose worker reads region-sized TIFF/PNG only; it
   * cannot open pyramidal slide formats (.ndpi/.svs) and would OOM on a multi-GB
   * slide. So instead of handing Cellpose the slide, we prepend a CROP step. The
   * server's CompoundService chains CROP's output into Cellpose's `@Input`
   * inputPaths and fans out (one Cellpose run per cropped region). No backend
   * change is needed — this mirrors the pipeline dialog's Run-on-Server path.
   *
   * @param cellPoseRequest  the v3 CellPoseRequest or v4 CellPoseSamRequest the
   *                         Cellpose page already built (its inputPaths are
   *                         dropped — the crop output supplies them)
   * @param cellProcessType  ProcessType.CELLPOSE or ProcessType.CELLPOSE_SAM —
   *                         selects the matching instruction @typeName
   * @param cropResolution   pyramid level to crop at (0 = full resolution)
   */
  runCellPoseWithCrop(cellPoseRequest: Request, cellProcessType: ProcessType,
                      cropResolution: number): Observable<any> {
    const rawData = this.filesService.getSelectedFileInfo()?.rawData;
    if (!rawData) {
      return new Observable(observer => observer.error('No file selected'));
    }

    const regions = (this.diagramComponent && this.diagramComponent.hasRegions())
      ? (this.diagramComponent.getRegionPolygons() || [])
      : [];

    const cropTypeName = TOOL_BY_PROCESS_TYPE.get(ProcessType.CROP)?.javaInstructionTypeName;
    const cellTypeName = TOOL_BY_PROCESS_TYPE.get(cellProcessType)?.javaInstructionTypeName;

    // Clone the Cellpose request as a plain object for the wire. Strip the
    // processType (the backend rejects it on the request body — see hackRequest)
    // and inputPaths (CompoundService fills these from the crop output).
    const cellRequestBody: any = { ...cellPoseRequest };
    delete cellRequestBody.processType;
    delete cellRequestBody.inputPaths;
    cellRequestBody.info = String(cellProcessType);

    const cropInstruction = {
      '@typeName': cropTypeName,
      file: rawData,
      request: {
        info: String(ProcessType.CROP),
        cropResolution: cropResolution || 0,
        regionSettings: {
          manualRegionResolution: 0,
          // No drawn regions → let the cropper auto-find tissue regions.
          findRegions: regions.length === 0,
          cropFoundRegionsAsRectangles: true,
          manualRegions: regions,
        },
      },
    };

    const cellInstruction = {
      '@typeName': cellTypeName,
      file: rawData,
      request: cellRequestBody,
    };

    const compoundInstruction: any = {
      '@typeName': 'org.jax.cimg.api.dao.instr.CompoundInstruction',
      file: rawData,
      request: {
        info: String(ProcessType.COMPOUND),
        // Child inputPaths are empty until CROP runs, so skip per-step
        // validation — chaining wires them at execution time.
        requireValidation: false,
        enableFanOut: true,
        requests: [cropInstruction, cellInstruction],
      },
    };

    return new Observable(observer => {
      this.process(ProcessType.COMPOUND, compoundInstruction as Instruction,
                   true, this.createStatusObserver());
      observer.next(true);
      observer.complete();
    });
  }

  /**
   * Find regions in the current image (if any)
   */
  find(request: Request, observer: Observer<Status>) {
    const processType = this.hackRequest(request);
    const instruction: Instruction | null = this.createInstruction(request);
    if (instruction === null) {
        return;
    }
    // TODO We may want to switch off the messages here and show
    // something else like a progress bar because this find will
    // be quick.
    this.process(processType, instruction, true, observer)
  }

  /**
   * Create the Instruction from the given Request parameter to be sent to the process service.
   *
   * @param request
   * @param fileOverride  use this file/folder instead of the currently
   *                      selected one - see runProcess.
   */
  createInstruction(request: Request, fileOverride?: RawFileInfo): Instruction | null {
    const fileInfo: IFileInfo | undefined = this.filesService.getSelectedFileInfo();
    const rawData = fileOverride ?? (fileInfo?.rawData ? fileInfo.rawData : undefined);
    if (rawData) {
      return {
        file: rawData,
        request: request
      };
    } else {
      return null;
    }
  }

  /**
   * Validate the given Instruction before sending it to the process service.
   *
   * @param processType
   * @param instruction
   */
  validateAndRun(processType: ProcessType, instruction: Instruction): Observable<any> {
    return new Observable(observer => {
      this.processService.validate(processType, instruction).subscribe({
        next: (validation) => {
          let runProcess = false;
          if (validation.type === ValidationType.VALID) {
            this.mainState.setValidationMessage(null);
            runProcess = true;
          } else if (validation.type === ValidationType.LIKELY_VALID) {
            console.warn(validation.message);
            this.mainState.setValidationMessage(null);
            runProcess = true;
          } else {
            if (validation.type === ValidationType.LIKELY_INVALID) {
              this.warningMessage('Warning Parameters', `${validation.message}`);
            } else {
              this.errorMessage('Invalid Parameters', `${validation.message}`);
            }
            this.mainState.setValidationMessage(validation.message);
          }

          if (runProcess) {
            // Stay pending until the /process response (or error) so callers' "submitting"
            // state covers the server-side review, not just the validate round trip.
            this.process(processType, instruction, true, this.createStatusObserver(), {
              next: () => {
                observer.next(validation);
                observer.complete();
              },
              error: (err) => observer.error(err),
            });
          } else {
            observer.error(validation);
          }
        },
        error: (error) => {
          this.errorMessage('Validation Failed', 'System was unable to validate the processing request');
          this.mainState.setValidationMessage('Validation request failed');
          observer.error(error);
        }
      });
    });
  }

  createStatusObserver() : Observer<Status> {
    return {
      next: () => {
        // Do Nothing
      },
      error: error => {
        console.error(`Error in submit observer: ${error}`);
      },
      complete: () => {
        // ok
      }
    }
  }

  /**
   * Sent the Instruction to the process service and manage the response.
   *
   * @param processType
   * @param instruction
   */
  process(processType: ProcessType, instruction: Instruction,
            requireMessage: boolean = true,  observer: Observer<Status>,
            onSettled?: { next: () => void; error: (err: unknown) => void }) {

        this.mainState.setProcessing(true);
        this.processService.run(processType, instruction).subscribe({
        next: (res: StringResponse) => {
          onSettled?.next();

          if (requireMessage) {
            const summary = 'Submit Success';
            const content = `${processType} process submitted.\n\nPlease visit 'Queue' to see progress.`;
            this.successMessage(summary, content);
          }
          if (res != null && res.response != undefined) {
            // Rebuild the poll URL against this app's own API base rather than
            // polling the server's absolute one — see monitorUrlFor (jit-ui#124).
            this.processService.monitor(this.processService.monitorUrlFor(res.response), observer);
          }
        },
        error: (err) => {
          onSettled?.error(err);
          const summary = 'Submit Failed';
          const content = '${processType} process failed to submit';
          if (requireMessage) {
            this.errorMessage(summary, content);
          }
        }
      });
  }

  /**
   * The service does not accept a request if the process type is included. It's actually only the EDoF
   * process that fails when it is included so we should request that to be fixed and we would no longer
   * need this function.
   *
   * @param request
   */
  hackRequest(request: Request): ProcessType {
    const processType = request.processType;
    // eslint-disable-next-line
    // @ts-ignore
    delete request.processType;
    return processType;
  }

  // TODO - this all shoud go into the Segmentation and HTQuant components which allows us to remove the
  // diagram component from the Facade
  private configRequest(request: Request) {
    if (request instanceof CropRequest) {
      const cropRequest = <CropRequest>request;
      // This is needed so that the cropping properly works
      // otherwise the backend defaults it to -1
      cropRequest.regionSettings.manualRegionResolution = 0;

      if (this.diagramComponent && this.diagramComponent.hasRegions()) {
        cropRequest.regionSettings.manualRegions = this.diagramComponent.getRegionPolygons();
      } else {
        cropRequest.regionSettings.manualRegions = [];
      }
      request = cropRequest;
    } else if (request instanceof QuantRequest) {
      const quantRequest = <QuantRequest>request;
      let regionCount = 0;
      // The visualizer publishes null when it is destroyed (sci-image-visualizer 0.9).
      if (this.diagramComponent && this.diagramComponent.hasRegions()) {
        // This is needed so that the cropping properly works
        // otherwise the backend defaults it to -1
        quantRequest.regionSeries = 0;
        const polygons: Polygon[] = this.diagramComponent.getRegionPolygons();
        if (polygons != null) {
          quantRequest.regions = [];
          polygons.forEach((poly: Polygon) => {
            if (poly == null) return;
            const region: QuantRegion = new QuantRegion();
            region.polygon = poly;
            regionCount += 1;
            region.name = 'Region_' + regionCount;

            quantRequest.regions.push(region);
          });
        }
      }
      request = quantRequest;

      // not sure if this is needed
      // if (quantRequest.outputName!=null) {
      //   this.previousOutputNames.push(quantRequest.outputName);
      // }
    }
  }

  download(): any {
    const selectedFile = this.filesService.getSelectedFileInfo();
    if (selectedFile) {
      const url: string | null = this.filesService.getDownloadUrl(selectedFile);
      if (url) {
        return this.downloadService.download(url);
      }
    }
  }

  save(blob: Blob, filename?: string): void {
    this.downloadService.saveBlob(blob, filename);
  }

  cancelDownload(): void {
    this.downloadService.cancelDownload();
  }

  public successMessage(title: string, content: string) {
    this.showMessage(title, content, 'success');
  }

  public warningMessage(title: string, content: string) {
    this.showMessage(title, content, 'warn');
  }

  public errorMessage(title: string, content: string) {
    this.showMessage(title, content, 'error');
  }

  private showMessage(title: string, content: string, severity: string) {
    this.messageService.add({ key: 'app-toast', severity: severity, summary: title, detail: content });
  }

  public setFileInfoZoomSession(fileInfoZoomSession: string) {
    this.mainState.setFileInfoZoomSession(fileInfoZoomSession);
  }
  public getFileInfoZoomSession() {
    return this.mainState.getFileInfoZoomSession();
  }

  searchFiles(request: FileSearchRequest): Observable<RawFileInfo> {
    return this.filesService.search(request);
  }

  /**
   * Entry point for opening a gs://bucket/path (or s3://, nio://) result URI
   * whose bucket may not be the currently-selected project - a pyjit deep
   * link (no project selected yet), the Results tab (results span every
   * project/bucket the user can access), or the Queue tab's "View Results"
   * (a queued job's result isn't necessarily in the currently-selected
   * project either).
   *
   * Always shows the file immediately via showResultInViewer()'s single
   * `GET /file-from-uri` call - project selection and the tree walk are
   * needed only to also reveal the node in the Files tab tree, not to
   * display it, so they no longer gate showing anything. This is what
   * made opening a deeply-nested result feel slow (a per-folder round trip
   * apiece, before any image loading could even start).
   *
   * `switchToFilesTab: false` (the Results tab row click) stops there.
   * `switchToFilesTab: true` (default: Queue tab, deep links, the Results
   * tab's "Show in Files tab" button) separately resolves the bucket to its
   * project, selects it if needed, waits for that project's root nodes to
   * load, then walks/expands the tree to *highlight* the node there
   * (setHighlightNode, not setSelectedNode - the viewer is already showing
   * the file from the fast path above, so this must not re-trigger it).
   */
  openResultPath(gcsPath: string, switchToFilesTab = true): void {
    this.showResultInViewer(gcsPath);
    if (!switchToFilesTab) return;

    const bucketMatch = gcsPath.match(/^(?:gs|s3|nio):\/\/([^/]+)\//);
    if (!bucketMatch) return;
    const bucket = bucketMatch[1];

    this.mainState.getProjects$().pipe(take(1)).subscribe(projects => {
      const project = projects.find(p => p.rawData?.project?.bucket === bucket);
      if (!project) return;

      this.mainState.getSelectedProject$().pipe(take(1)).subscribe(selected => {
        if (selected?.rawData?.project?.bucket === bucket) {
          this.navigateToResults(gcsPath, true, true);
          return;
        }
        this.selectProject(project);
        this.mainState.getRootNodes$().pipe(
          filter(nodes => nodes.length > 0),
          take(1)
        ).subscribe(() => this.navigateToResults(gcsPath, true, true));
      });
    });
  }

  /**
   * Resolves gcsPath's own metadata in one call (FilesService.getFileInfoFromUri
   * → jit-service `GET /file-from-uri`) and selects it, which is all
   * FileViewerComponent needs to display it (it reacts to getSelectedNode$()
   * regardless of which tab is active) - no project switch, no tree walk.
   * The resulting node has no `.parent`, so it is only fit for display, not
   * for tree-relative operations like ProcessComponentBase.createRule()'s
   * folder fallback.
   *
   * Hands off to openResultRequest$ (see its doc, constructor) rather than
   * subscribing here directly, so a newer call cancels an older one still
   * in flight instead of letting both race to setSelectedNode().
   */
  private showResultInViewer(gcsPath: string): void {
    this.openResultRequest$.next(gcsPath);
  }

  /**
   * Navigate to a GCS result path (gs://bucket/path) in the file tree.
   * Strips the scheme and bucket prefix, then delegates to navigateToFileInTree.
   * `switchToFilesTab` is false for the Results tab's row click, which shows
   * the file in the viewer in place without leaving the Results tab - see
   * ResultSearchComponent.openRow(). `highlightOnly` - see traverseToNode.
   */
  navigateToResults(gcsPath: string, switchToFilesTab = true, highlightOnly = false): void {
    const relPath = gcsPath.replace(/^(gs|s3|nio):\/\/[^/]+\//, '');
    if (relPath) {
      this.navigateToFileInTree({ relPath } as IFileInfo, switchToFilesTab, highlightOnly);
    }
  }

  /**
   * Walk the tree, expanding folders as needed, until the node matching
   * fileInfo.relPath is found, then select or highlight it - see
   * traverseToNode. Also switches to the Files tab by default so the node
   * is visible there too; pass `switchToFilesTab: false` for a caller (e.g.
   * the Results tab) that wants the file shown in the viewer without
   * navigating away from its own tab.
   */
  navigateToFileInTree(fileInfo: IFileInfo, switchToFilesTab = true, highlightOnly = false): void {
    if (switchToFilesTab) {
      this.mainState.getMainTabMenu$().pipe(take(1)).subscribe(items => {
        const filesItem = items.find(i => i.id === 'files');
        if (filesItem) this.mainState.setSelectedTabItemL$(filesItem);
      });
    }

    const segments = fileInfo.relPath.split('/').filter(s => s.length > 0);
    if (segments.length === 0) return;

    this.mainState.getRootNodes$().pipe(take(1)).subscribe(rootNodes => {
      this.traverseToNode(rootNodes, segments, 0, highlightOnly);
    });
  }

  /**
   * `highlightOnly` picks the terminal action once the target node is
   * found: `setSelectedNode` (default) both shows it in the viewer (which
   * reacts to getSelectedNode$()) and highlights it in the file tree - the
   * normal Search-tab/deep-link behavior. `setHighlightNode` only
   * highlights it in the tree, for a caller (openResultPath's Files-tab
   * reveal) that already showed the file via the fast showResultInViewer()
   * path and must not trigger a second, redundant reload of it.
   */
  private traverseToNode(
    nodes: TreeNode<IFileInfo>[], segments: string[], depth: number, highlightOnly = false,
  ): void {
    if (depth >= segments.length) return;

    const segment = segments[depth];
    const targetNode = nodes.find(n => n.data?.name === segment);
    if (!targetNode) return;

    if (depth === segments.length - 1) {
      if (highlightOnly) {
        this.mainState.setHighlightNode(targetNode);
      } else {
        this.mainState.setSelectedNode(targetNode);
      }
      return;
    }

    // Need to go deeper — expand this folder
    if (targetNode.children && targetNode.children.length > 0) {
      targetNode.expanded = true;
      this.traverseToNode(targetNode.children, segments, depth + 1, highlightOnly);
    } else {
      this.mainState.setFileTreeLoading(true);
      this.filesService.getChildren(targetNode.data!.rawData, 0, PAGE_SIZE)
        .pipe(finalize(() => this.mainState.setFileTreeLoading(false)))
        .subscribe({
        next: files => {
          const fileArr: IFileInfo[] = files.map(f => FileInfoFactory.getFileInfo(f));
          const newNodes = this.filesService.createNodes(fileArr);
          newNodes.forEach(n => n.parent = targetNode);
          targetNode.children = [...newNodes];
          targetNode.expanded = true;
        },
        complete: () => {
          this.traverseToNode(targetNode.children || [], segments, depth + 1, highlightOnly);
        },
        error: (err: HttpErrorResponse) => {
          const msg = err?.error?.message ?? err?.message ?? 'Could not open folder';
          this.errorMessage('Could not open folder', msg);
          console.error('getChildren failed', err);
        }
      });
    }
  }

}
